import React, {useMemo, useState} from 'react';
import {Link as RouterLink} from 'react-router';
import {
    Button,
    CircularProgress,
    Link as MuiLink,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import TabLoading from './TabLoading';
import TabEmpty from './TabEmpty';
import PropertyEditorDialog from './PropertyEditorDialog';
import PropertyDeleteDialog from './PropertyDeleteDialog';
import {usePagination} from './usePagination';
import TabPagination from './TabPagination';

const DEFAULT_MAX_PINNED_PROPERTIES = 3;

function normalizeEntityRef(ref) {
    if (ref == null) return ref;
    const trimmed = String(ref).trim();
    if (/^q\d+$/i.test(trimmed)) return trimmed.toUpperCase();
    return trimmed;
}

function hasDirectedChartConnection(connections, fromRef, toRef) {
    const from = normalizeEntityRef(fromRef);
    const to = normalizeEntityRef(toRef);
    if (!from || !to) return false;
    return (connections || []).some((connection) => (
        normalizeEntityRef(connection.from) === from
        && normalizeEntityRef(connection.to) === to
    ));
}

function resolvePropertyChartActionState(property, {sourceEntityRef, chartEntityIds, chartConnections}) {
    const valueEntityId = property?.valueEntityId;
    if (!valueEntityId || !sourceEntityRef) return null;

    const valueRef = normalizeEntityRef(valueEntityId);
    const sourceRef = normalizeEntityRef(sourceEntityRef);
    const valueOnChart = chartEntityIds.has(valueRef);
    // Property C on A with value B is drawn as B --C--> A ("B is C of A").
    const connected = hasDirectedChartConnection(chartConnections, valueRef, sourceRef);

    if (connected) return 'connected';
    if (valueOnChart) return 'connect';
    return 'add';
}

const PropertyChartAction = ({
    actionState,
    chartReady,
    busy,
    onClick,
}) => {
    if (!actionState) return null;

    if (actionState === 'connected') {
        return (
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{color: 'success.main', whiteSpace: 'nowrap'}}>
                <CheckCircleOutlineIcon sx={{fontSize: 16}}/>
                <Typography variant="body2" sx={{fontWeight: 600, fontSize: 13}}>
                    Connected
                </Typography>
            </Stack>
        );
    }

    const label = actionState === 'connect' ? 'Connect on chart' : 'Add to chart';

    return (
        <Button
            size="small"
            variant="text"
            onClick={onClick}
            disabled={!chartReady || busy}
            startIcon={busy ? <CircularProgress size={14}/> : null}
            sx={{whiteSpace: 'nowrap'}}
        >
            {label}
        </Button>
    );
};

/**
 * Properties tab — renders entity properties with internal links for entity-value references.
 */
const PropertiesTab = ({
                           properties,
                           loading,
                           onCreateProperty,
                           onUpdateProperty,
                           onDeleteProperty,
                           saving = false,
                           error,
                           crudEnabled = false,
                           pinEnabled = false,
                           pinnedPropertyIds = [],
                           onPinProperty,
                           onUnpinProperty,
                           maxPinnedProperties = DEFAULT_MAX_PINNED_PROPERTIES,
                           chartActionsEnabled = false,
                           chartReady = false,
                           sourceEntityRef = null,
                           chartEntityIds = null,
                           chartConnections = null,
                           onAddPropertyToChart,
                           addingPropertyId = null,
                       }) => {
    const [editorOpen, setEditorOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [editingProperty, setEditingProperty] = useState(null);
    const [deletingProperty, setDeletingProperty] = useState(null);

    const sortedProperties = useMemo(() => properties || [], [properties]);
    const {page, setPage, pageItems, totalPages, hasPagination} = usePagination(sortedProperties);
    const pinnedIdSet = useMemo(() => new Set(pinnedPropertyIds || []), [pinnedPropertyIds]);
    const chartEntityIdSet = useMemo(
        () => new Set(Array.isArray(chartEntityIds) ? chartEntityIds : []),
        [chartEntityIds],
    );
    const chartConnectionList = useMemo(
        () => (Array.isArray(chartConnections) ? chartConnections : []),
        [chartConnections],
    );
    const showActions = crudEnabled || pinEnabled || chartActionsEnabled;
    const atPinCap = pinnedIdSet.size >= maxPinnedProperties;

    const handleAddClick = () => {
        setEditingProperty(null);
        setEditorOpen(true);
    };

    const handleEditClick = (property) => {
        setEditingProperty(property);
        setEditorOpen(true);
    };

    const handleDeleteClick = (property) => {
        setDeletingProperty(property);
        setDeleteOpen(true);
    };

    const handleEditorSubmit = async (payload) => {
        try {
            if (editingProperty?.propertyItemId) {
                await onUpdateProperty?.(editingProperty.propertyItemId, payload);
            } else {
                await onCreateProperty?.(payload);
            }
            setEditorOpen(false);
            setEditingProperty(null);
        } catch (submitError) {
            // Keep dialog open so user can retry after terminal failures.
        }
    };

    const handleDeleteConfirm = async () => {
        if (!deletingProperty?.propertyItemId) {
            setDeleteOpen(false);
            return;
        }

        try {
            await onDeleteProperty?.(deletingProperty.propertyItemId);
            setDeleteOpen(false);
            setDeletingProperty(null);
        } catch (submitError) {
            // Keep dialog open so user can retry after terminal failures.
        }
    };

    if (loading) return <TabLoading/>;

    return (
        <Stack>
            <Stack direction="row" justifyContent="space-between" alignItems="center" p={2}>
                <Typography variant="subtitle2" color="text.secondary">
                    {crudEnabled ? 'Manage entity properties' : 'Entity properties (read-only)'}
                    {pinEnabled ? ` · Pin up to ${maxPinnedProperties} on the card` : ''}
                </Typography>
                {crudEnabled && (
                    <Button variant="contained" size="small" onClick={handleAddClick} disabled={saving}>
                        Add property
                    </Button>
                )}
            </Stack>

            {sortedProperties.length === 0 ? (
                <TabEmpty message="No properties found for this entity."/>
            ) : (
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{fontWeight: 600}}>Property</TableCell>
                                <TableCell sx={{fontWeight: 600}}>Value</TableCell>
                                {showActions && (
                                    <TableCell sx={{fontWeight: 600, minWidth: 320}} align="right">Actions</TableCell>
                                )}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {pageItems.map((p, idx) => {
                                const isPinned = pinnedIdSet.has(p.propertyItemId);
                                const chartActionState = chartActionsEnabled
                                    ? resolvePropertyChartActionState(p, {
                                        sourceEntityRef,
                                        chartEntityIds: chartEntityIdSet,
                                        chartConnections: chartConnectionList,
                                    })
                                    : null;
                                return (
                                    <TableRow key={p.propertyItemId || (p.propertyId ? `${p.propertyId}-${idx}` : idx)}>
                                        <TableCell>{p.propertyLabel || p.propertyId || p.property}</TableCell>
                                        <TableCell>
                                            <PropertyValue property={p}/>
                                        </TableCell>
                                        {showActions && (
                                            <TableCell align="right">
                                                <Stack
                                                    direction="row"
                                                    spacing={1}
                                                    justifyContent="flex-end"
                                                    flexWrap="nowrap"
                                                    sx={{whiteSpace: 'nowrap'}}
                                                >
                                                    {chartActionsEnabled ? (
                                                        <PropertyChartAction
                                                            actionState={chartActionState}
                                                            chartReady={chartReady}
                                                            busy={addingPropertyId === p.propertyItemId}
                                                            onClick={() => onAddPropertyToChart?.(p)}
                                                        />
                                                    ) : null}
                                                    {pinEnabled && p.propertyItemId ? (
                                                        <Button
                                                            size="small"
                                                            variant="text"
                                                            onClick={() => (
                                                                isPinned
                                                                    ? onUnpinProperty?.(p)
                                                                    : onPinProperty?.(p)
                                                            )}
                                                            disabled={!isPinned && atPinCap}
                                                            sx={{whiteSpace: 'nowrap'}}
                                                        >
                                                            {isPinned ? 'Unpin' : 'Pin to card'}
                                                        </Button>
                                                    ) : null}
                                                    {crudEnabled && (
                                                        <>
                                                            <Button
                                                                size="small"
                                                                variant="text"
                                                                onClick={() => handleEditClick(p)}
                                                                disabled={saving}
                                                                sx={{whiteSpace: 'nowrap'}}
                                                            >
                                                                Edit
                                                            </Button>
                                                            <Button
                                                                size="small"
                                                                color="error"
                                                                variant="text"
                                                                onClick={() => handleDeleteClick(p)}
                                                                disabled={saving}
                                                                sx={{whiteSpace: 'nowrap'}}
                                                            >
                                                                Delete
                                                            </Button>
                                                        </>
                                                    )}
                                                </Stack>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
            <TabPagination
                hasPagination={hasPagination}
                page={page}
                totalPages={totalPages}
                totalItems={sortedProperties.length}
                onChange={setPage}
            />

            {crudEnabled && (
                <PropertyEditorDialog
                    open={editorOpen}
                    mode={editingProperty ? 'edit' : 'create'}
                    property={editingProperty}
                    saving={saving}
                    error={error}
                    onCancel={() => {
                        setEditorOpen(false);
                        setEditingProperty(null);
                    }}
                    onSubmit={handleEditorSubmit}
                />
            )}

            {crudEnabled && (
                <PropertyDeleteDialog
                    open={deleteOpen}
                    property={deletingProperty}
                    saving={saving}
                    onCancel={() => {
                        setDeleteOpen(false);
                        setDeletingProperty(null);
                    }}
                    onConfirm={handleDeleteConfirm}
                />
            )}
        </Stack>
    );
};

/**
 * Renders a single property value — internal link, external link, or plain text.
 */
const PropertyValue = ({property: p}) => {
    if (p.valueEntityId) {
        return (
            <MuiLink component={RouterLink} to={`/kb/${p.valueEntityId}`} underline="hover">
                {p.value}
            </MuiLink>
        );
    }

    if (p.value?.startsWith?.('http')) {
        return (
            <MuiLink href={p.value} target="_blank" rel="noopener noreferrer">
                {p.valueLabel || p.value}
            </MuiLink>
        );
    }

    return <>{p.value || '—'}</>;
};

export default PropertiesTab;
